import { Injectable } from "@angular/core";
import { NavigationError } from "@angular/router";

const RELOAD_STORAGE_KEY = "tp-chunk-recovery-at";
const RELOAD_COOLDOWN_MS = 60_000;

/** Recover an open tab whose lazy route chunks disappeared during a deploy. */
@Injectable({ providedIn: "root" })
export class ChunkLoadRecoveryService {
  private reloading = false;

  handleNavigationError(event: NavigationError): void {
    if (
      typeof window === "undefined" ||
      this.reloading ||
      window.navigator.onLine === false ||
      !isChunkLoadError(event.error)
    ) {
      return;
    }

    // Use the attempted destination, including query and fragment. Angular may
    // already have restored the previous address when this handler runs.
    const destination = new URL(event.url, document.baseURI);
    if (destination.origin !== window.location.origin) return;

    // Persist BEFORE leaving: a bad deployment must not cause a reload loop,
    // even when the new document fails on its initial navigation. Do not clear
    // on NavigationEnd (a redirect could succeed before another chunk fails).
    try {
      const now = Date.now();
      const previous = Number(window.sessionStorage.getItem(RELOAD_STORAGE_KEY));
      if (previous > 0 && now - previous < RELOAD_COOLDOWN_MS) return;
      window.sessionStorage.setItem(RELOAD_STORAGE_KEY, String(now));
    } catch {
      // Without persistent loop protection, leave normal error handling intact.
      return;
    }

    this.reloading = true;
    this.reloadPage(destination.href);
  }

  /** Separate browser navigation so tests can exercise the real router hook. */
  private reloadPage(url: string): void {
    window.location.assign(url);
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /error loading dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message) ||
    (error.name === "ChunkLoadError" && /Loading chunk .+ failed/i.test(error.message))
  );
}
